import React, { useMemo, useState, useRef, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import Editor, {
  BtnBold,
  BtnBulletList,
  BtnItalic,
  BtnNumberedList,
  BtnStrikeThrough,
  BtnStyles,
  BtnUnderline,
  createButton,
  Separator,
  Toolbar,
  useEditorState
} from 'react-simple-wysiwyg';
import { FaIndent, FaLink, FaOutdent, FaQuoteLeft } from 'react-icons/fa';
import { IoMdClose } from "react-icons/io";
import { Rnd } from 'react-rnd';
import './PopupModal.scss';
import sanitizeNoteContent from './noteContentSanitizer';

const hasSelectedText = () => {
  const selection = globalThis.getSelection();
  return selection && selection.rangeCount > 0 && selection.toString().trim().length > 0;
};

const toSafeLinkUrl = (url) => {
  const value = (url || '').trim();

  if (!value) {
    return '';
  }

  return /^(https?:|mailto:)/i.test(value) ? value : `https://${value}`;
};

const BtnBlockquote = createButton('Block quote', <FaQuoteLeft aria-hidden="true" />, () => document.execCommand('formatBlock', false, 'BLOCKQUOTE')); // NOSONAR
const BtnOutdent = createButton('Decrease indent', <FaOutdent aria-hidden="true" />, 'outdent');
const BtnIndent = createButton('Increase indent', <FaIndent aria-hidden="true" />, 'indent');
const BtnNoteLink = createButton('Link', <FaLink aria-hidden="true" />, () => {
  if (!hasSelectedText()) {
    return;
  }

  document.execCommand('createLink', false, toSafeLinkUrl(globalThis.prompt('URL', ''))); // NOSONAR
});

const SelectionLinkButton = () => {
  const editorState = useEditorState();

  if (!editorState.$selection || !hasSelectedText()) {
    return null;
  }

  return (
    <>
      <Separator />
      <BtnNoteLink />
    </>
  );
};

const ResizableModal = ({ open, setOpen, content, setContent, modalChecked, setModalPosition, modalPosition, setModalChecked }) => {
  const [isDraggable, setIsDraggable] = useState(true);
  const editorRef = useRef(null);
  const modalRef = useRef(null);
  const sanitizedContent = useMemo(() => sanitizeNoteContent(content), [content]);

  const handleContentChange = (event) => {
    setContent(sanitizeNoteContent(event.target.value));
  };

  const blurEditor = () => {
    if (editorRef.current) {
      editorRef.current.blur();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isEditorClick = modalRef.current?.contains(event.target);

      if (!isEditorClick) {
        setIsDraggable(true);
        blurEditor();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div>
      {open && (
        <Rnd
          className='popup-modal'
          size={{ width: modalPosition.width, height: modalPosition.height }}
          position={{ x: modalPosition.x, y: modalPosition.y }}
          onDragStop={(e, d) => {
            setModalPosition({ ...modalPosition, x: d.x, y: d.y });
          }}
          onResizeStop={(e, direction, ref, delta, position) => {
            setModalPosition({ ...modalPosition, height: ref.offsetHeight, width: ref.offsetWidth });
          }}
          disableDragging={!isDraggable}
          minWidth={310}
          minHeight={250}
          bounds="window"
        >
          <div className='container-div'>
            <h3 className='popup-header'>Blockly Script Notes</h3>
            <div className='header-div'>
              <IoMdClose
                size={24}
                className='close-icon'
                onClick={() => setOpen()}
              />
            </div>
          </div>
          <div ref={modalRef} className='notes-editor-container-div'>
            <div className='notes-editor-container'>
              <Editor
                ref={editorRef}
                value={sanitizedContent}
                onChange={handleContentChange}
                placeholder='Use this modal to add comments describing your Blockly script'
                className='notes-editor'
                onFocus={() => setIsDraggable(false)}
              >
                <Toolbar>
                  <BtnStyles />
                  <Separator />
                  <BtnBold />
                  <BtnItalic />
                  <BtnUnderline />
                  <BtnStrikeThrough />
                  <BtnBlockquote />
                  <Separator />
                  <BtnNumberedList />
                  <BtnBulletList />
                  <BtnOutdent />
                  <BtnIndent />
                  <SelectionLinkButton />
                </Toolbar>
              </Editor>
              <div className='label-div'>
                <label className='popup-label'>
                  <input
                    type="checkbox"
                    checked={modalChecked}
                    onChange={(e) => setModalChecked(e.target.checked)}
                  />
                  Show on Launch
                </label>
              </div>
            </div>
          </div>
        </Rnd>
      )}
    </div>
  );
};

export default withRouter(ResizableModal);